import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useSearchSeries } from '../../hooks/useSeries';
import { useContentPixelTracking } from '../../hooks/usePixelTracking';
import { RequestSeriesModal } from '../../components/RequestSeriesModal';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../../theme/netflixTheme';

export default function SearchScreen(): JSX.Element {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isRequestSeriesModalOpen, setIsRequestSeriesModalOpen] = useState(false);
  const { trackSearch } = useContentPixelTracking();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
      if (inputValue.length > 0) {
        trackSearch(inputValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, trackSearch]);

  const searchParams = useMemo(() => ({ limit: 12 }), []);

  const { data: resultsData, isLoading: searchLoading } = useSearchSeries(debouncedQuery, searchParams);

  const results = resultsData?.data || [];

  return (
    <>
    <ScrollView style={styles.container} scrollEventThrottle={16} showsVerticalScrollIndicator={false}>
      <div style={searchWrapperStyle}>
        <View style={styles.searchSection}>
          <View style={styles.searchInputContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="Buscar séries..."
              placeholderTextColor={NETFLIX_COLORS.textTertiary}
              value={inputValue}
              onChangeText={setInputValue}
              autoFocus
            />
          </View>
        </View>
      </div>

      <div style={mainWrapperStyle}>
        {searchLoading && debouncedQuery.length > 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
            <Text style={styles.loadingText}>Buscando...</Text>
          </View>
        ) : results.length > 0 ? (
          <>
            <Text style={styles.resultCount}>
              {results.length} resultado{results.length !== 1 ? 's' : ''}
            </Text>
            <div style={gridStyle}>
              {results.map((item) => (
                <SeriesCard key={item.id} series={item} onPress={() => navigate(`/series/${item.id}`)} />
              ))}
            </div>
          </>
        ) : debouncedQuery.length > 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>Nenhuma série encontrada para "{debouncedQuery}"</Text>
            <Text style={styles.emptySubtitle}>Tente outro termo de busca</Text>
            <TouchableOpacity
              style={styles.requestButton}
              onPress={() => setIsRequestSeriesModalOpen(true)}
            >
              <Text style={styles.requestButtonText}>Solicitar esta série</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>Explorar Séries</Text>
            <Text style={styles.emptySubtitle}>Digite acima para buscar seus conteúdos favoritos</Text>
          </View>
        )}
      </div>

      <View style={styles.bottomPadding} />
    </ScrollView>

    {/* Request Series Modal */}
    <RequestSeriesModal
      isOpen={isRequestSeriesModalOpen}
      onClose={() => setIsRequestSeriesModalOpen(false)}
      searchQuery={debouncedQuery}
    />
    </>
  );
}

interface SeriesCardProps {
  series: any;
  onPress: () => void;
}

function SeriesCard({ series, onPress }: SeriesCardProps) {
  return (
    <TouchableOpacity style={styles.seriesCard} onPress={onPress}>
      {series.poster_url ? (
        <Image source={{ uri: series.poster_url }} style={styles.seriesPoster} />
      ) : (
        <View style={[styles.seriesPoster, styles.posterPlaceholder]}>
          <Text style={styles.posterIcon}>—</Text>
        </View>
      )}
      <View style={styles.cardInfo}>
        <Text style={styles.cardTitle} numberOfLines={2}>
          {series.title}
        </Text>
        <View style={styles.cardMeta}>
          <Text style={styles.rating}>{series.rating.toFixed(1)}</Text>
          <Text style={styles.year}>{series.release_year}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const searchWrapperStyle: React.CSSProperties = {
  maxWidth: '100%',
  backgroundColor: NETFLIX_COLORS.background,
  borderBottomWidth: 0,
  padding: `${NETFLIX_SPACING.xl}px 0`,
};

const mainWrapperStyle: React.CSSProperties = {
  maxWidth: '1440px',
  margin: '0 auto',
  width: '100%',
  padding: `0 ${NETFLIX_SPACING.xl}px`,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
  gap: `${NETFLIX_SPACING.lg}px`,
  marginTop: NETFLIX_SPACING.lg,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NETFLIX_COLORS.background,
  },
  searchSection: {
    maxWidth: 1440,
    marginHorizontal: 'auto',
    paddingHorizontal: NETFLIX_SPACING.xl,
  },
  searchInputContainer: {
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 12,
    padding: NETFLIX_SPACING.lg,
    paddingLeft: NETFLIX_SPACING.xl,
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 15,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  loadingContainer: {
    minHeight: 300,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 14,
    marginTop: NETFLIX_SPACING.lg,
  },
  resultCount: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 16,
    fontWeight: '600',
    marginTop: NETFLIX_SPACING.xl,
    marginBottom: NETFLIX_SPACING.lg,
  },
  emptyContainer: {
    minHeight: 300,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: NETFLIX_SPACING.xxxl,
  },
  emptyTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 20,
    fontWeight: '600',
    marginBottom: NETFLIX_SPACING.md,
  },
  emptySubtitle: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 14,
  },
  seriesCard: {
    width: '100%',
  },
  seriesPoster: {
    width: '100%',
    height: 240,
    borderRadius: 8,
    backgroundColor: NETFLIX_COLORS.surface,
  },
  posterPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  posterIcon: {
    fontSize: 48,
    color: NETFLIX_COLORS.textTertiary,
  },
  cardInfo: {
    paddingTop: NETFLIX_SPACING.md,
  },
  cardTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
    fontWeight: '500',
    marginBottom: NETFLIX_SPACING.sm,
    lineHeight: 18,
  },
  cardMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rating: {
    color: NETFLIX_COLORS.accent,
    fontSize: 12,
    fontWeight: '600',
  },
  year: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 11,
  },
  bottomPadding: {
    height: 50,
  },
  requestButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.md,
    borderRadius: 8,
    marginTop: NETFLIX_SPACING.lg,
  },
  requestButtonText: {
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    fontWeight: '700',
  },
});
